import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Link,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  PhotoLibrary as PhotoIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  DirectionsBike as VehicleIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { vehicleService, Vehicle, MediaFile } from '../services/vehicleService';
import { riderService } from '../services';
import { batteryService, Battery } from '../services/batteryService';
import RiderHistoryTimeline from '../components/vehicles/RiderHistoryTimeline';
import sparePartsApi, { ApiResponse } from '../api/spareParts.api';

interface ServiceRecord {
  id: string;
  serviceType: string;
  serviceDate: string;
  description: string;
  workPerformed?: string;
  serviceStatus: string;
  cost?: number;
  serviceCenter?: string;
  technicianName?: string;
  nextServiceDate?: string;
  source?: 'VEHICLE_SERVICE' | 'SPARE_PARTS';
  referenceCode?: string;
}

interface SparePartsTicket {
  id: string;
  code: string;
  vehicleId: string;
  warehouseId: string;
  serviceType: 'ON_ROAD' | 'PREVENTIVE' | 'IN_HOUSE';
  status: string;
  title: string;
  description: string;
  assignedToId?: string | null;
  actualRepairCost?: number | string | null;
  serviceInvoice?: {
    totalAmount?: number | string | null;
  } | null;
  resolution?: string | null;
  createdAt: string;
  completedAt?: string | null;
  closedAt?: string | null;
}

interface PreventiveScheduleHistory {
  id: string;
  code: string;
  vehicleId: string;
  lastServiceDate: string;
  nextServiceDate: string;
  status: string;
  remarks?: string | null;
  cycles?: Array<{
    id: string;
    dueDate: string;
    status: string;
    generatedTicketId?: string | null;
  }>;
}

const normalizeVehicleNumber = (value?: string | null) =>
  String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const integrationPersonName = (employee: any) => {
  const userName = [employee?.user?.firstName, employee?.user?.lastName].filter(Boolean).join(' ');
  const directName = [employee?.firstName, employee?.lastName].filter(Boolean).join(' ');
  const name = employee?.employeeName || employee?.name || employee?.fullName || directName || userName;
  const code = employee?.employeeCode || employee?.employeeId;

  return name ? (code ? `${name} (${code})` : name) : undefined;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vehicle-tabpanel-${index}`}
      aria-labelledby={`vehicle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const VehicleProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [freshMediaFiles, setFreshMediaFiles] = useState<MediaFile[]>([]);
  const [serviceHistory, setServiceHistory] = useState<ServiceRecord[]>([]);
  const [sparePartsHistory, setSparePartsHistory] = useState<ServiceRecord[]>([]);
  const [batteries, setBatteries] = useState<Battery[]>([]);
  const [currentRiderName, setCurrentRiderName] = useState<string>('');
  const [currentRiderPublicId, setCurrentRiderPublicId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [sparePartsHistoryLoading, setSparePartsHistoryLoading] = useState(false);
  const [batteryLoading, setBatteryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Helper function to format dates in DD/MM/YYYY format
  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString('en-GB'); // British English uses DD/MM/YYYY format
  };

  useEffect(() => {
    if (id) {
      fetchVehicleData();
      fetchMediaFiles();
      fetchServiceHistory();
      fetchBatteries();
    }
  }, [id]);

  useEffect(() => {
    if (vehicle?.registrationNumber) {
      fetchSparePartsHistory(vehicle.registrationNumber);
    }
  }, [vehicle?.registrationNumber]);

  // Auto-refresh presigned URLs every 50 minutes (before 1 hour expiry)
  useEffect(() => {
    if (!id) return;

    const refreshInterval = setInterval(() => {
      refreshPresignedUrls();
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, [id]);

  // Update freshMediaFiles when mediaFiles changes
  useEffect(() => {
    setFreshMediaFiles(mediaFiles);
  }, [mediaFiles]);

  // Function to refresh presigned URLs
  const refreshPresignedUrls = useCallback(async () => {
    if (!id) return;

    try {
      const response = await vehicleService.getMediaFiles(id);
      if (response.success && response.data) {
        setFreshMediaFiles(response.data);
        console.log('Vehicle document presigned URLs refreshed successfully');
      }
    } catch (error) {
      console.error('Error refreshing vehicle document presigned URLs:', error);
    }
  }, [id]);

  // Function to handle viewing a document with fresh URL
  const handleViewDocument = useCallback(async (mediaFile: MediaFile) => {
    if (!id) return;

    try {
      // First, try to use the presigned URL directly from mediaFile
      if (mediaFile.url) {
        window.open(mediaFile.url, '_blank');

        // Also refresh URLs in the background for next time
        try {
          const response = await vehicleService.getMediaFiles(id);
          if (response.success && response.data) {
            setFreshMediaFiles(response.data);
          }
        } catch (error) {
          console.warn('Background refresh failed:', error);
        }
        return;
      }

      // Fallback: Fetch fresh presigned URL from server
      const response = await vehicleService.getMediaFiles(id);
      if (response.success && response.data) {
        const freshMediaList = response.data;
        const freshMedia = freshMediaList.find((m: MediaFile) => m.id === mediaFile.id);

        if (freshMedia?.url) {
          window.open(freshMedia.url, '_blank');
          // Update the state with fresh URLs
          setFreshMediaFiles(freshMediaList);
        } else {
          console.error('No URL available for document');
        }
      }
    } catch (error) {
      console.error('Error viewing document:', error);
    }
  }, [id]);

  const fetchVehicleData = async () => {
    try {
      setLoading(true);
      const response = await vehicleService.getVehicle(id!);
      console.log('Vehicle data received:', response.data);
      console.log('Hub data:', response.data.hub);
      console.log('Insurance data:', response.data.insuranceDetails);
      setVehicle(response.data);

      // Fetch rider name if there's a currentRiderId
      if (response.data.currentRiderId) {
        fetchCurrentRiderName(response.data.currentRiderId);
      }
    } catch (error: any) {
      console.error('Error fetching vehicle:', error);
      setError('Failed to fetch vehicle data');
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceHistory = async () => {
    try {
      setServiceLoading(true);
      const response = await vehicleService.getVehicleServiceHistory(id!);
      console.log('Service history received:', response.data);
      setServiceHistory(response.data?.serviceHistory || []);
    } catch (error: any) {
      console.error('Error fetching service history:', error);
      // Don't set a general error for service history, just log it
      setServiceHistory([]);
    } finally {
      setServiceLoading(false);
    }
  };

  const fetchSparePartsHistory = async (registrationNumber: string) => {
    try {
      setSparePartsHistoryLoading(true);

      const [ticketsResponse, schedulesResponse] = await Promise.all([
        sparePartsApi.get<ApiResponse<SparePartsTicket[]>>('/maintenance/service-tickets', {
          params: { vehicleId: registrationNumber, page: 1, limit: 100 },
        }),
        sparePartsApi.get<ApiResponse<PreventiveScheduleHistory[]>>('/maintenance/preventive-schedules', {
          params: { vehicleId: registrationNumber, page: 1, limit: 100 },
        }),
      ]);
      const [hubsResult, techniciansResult] = await Promise.allSettled([
        sparePartsApi.get<ApiResponse<any[]>>('/integrations/hubs'),
        sparePartsApi.get<ApiResponse<any[]>>('/integrations/technicians'),
      ]);

      const normalizedRegistration = normalizeVehicleNumber(registrationNumber);
      const hubs = hubsResult.status === 'fulfilled' ? hubsResult.value.data.data || [] : [];
      const technicians = techniciansResult.status === 'fulfilled'
        ? techniciansResult.value.data.data || []
        : [];
      const hubNameById = new Map(hubs.map((hub: any) => [
        hub.id,
        hub.code ? `${hub.name} (${hub.code})` : hub.name,
      ]));
      const technicianNameById = new Map<string, string>();

      technicians.forEach((employee: any) => {
        const name = integrationPersonName(employee);
        if (!name) return;

        [employee.id, employee.userId, employee.employeeId, employee.employeeRecordId]
          .filter(Boolean)
          .forEach((key: string) => technicianNameById.set(String(key), name));
      });

      const ticketRecords: ServiceRecord[] = (ticketsResponse.data.data || [])
        .filter((ticket) => normalizeVehicleNumber(ticket.vehicleId) === normalizedRegistration)
        .map((ticket) => ({
        id: `spare-ticket-${ticket.id}`,
        source: 'SPARE_PARTS',
        referenceCode: ticket.code,
        serviceType: ticket.serviceType.replace('_', ' '),
        serviceDate: ticket.closedAt || ticket.completedAt || ticket.createdAt,
        description: ticket.description || ticket.title,
        workPerformed: ticket.resolution || ticket.title,
        serviceStatus: ticket.status,
        cost: ticket.serviceInvoice?.totalAmount == null
          ? (ticket.actualRepairCost == null ? undefined : Number(ticket.actualRepairCost))
          : Number(ticket.serviceInvoice.totalAmount),
        serviceCenter: hubNameById.get(ticket.warehouseId) || 'Assigned hub',
        technicianName: ticket.assignedToId
          ? technicianNameById.get(ticket.assignedToId) || 'Assigned technician'
          : 'Not assigned',
      }));

      const scheduleRecords: ServiceRecord[] = (schedulesResponse.data.data || [])
        .filter((schedule) => normalizeVehicleNumber(schedule.vehicleId) === normalizedRegistration)
        .map((schedule) => {
        const latestCycle = schedule.cycles?.[0];

        return {
          id: `preventive-schedule-${schedule.id}`,
          source: 'SPARE_PARTS',
          referenceCode: schedule.code,
          serviceType: 'Preventive Schedule',
          serviceDate: latestCycle?.dueDate || schedule.nextServiceDate,
          description: schedule.remarks || 'Preventive maintenance schedule',
          serviceStatus: latestCycle?.status || schedule.status,
          serviceCenter: 'Scheduled maintenance',
          nextServiceDate: schedule.nextServiceDate,
        };
      });

      setSparePartsHistory([...ticketRecords, ...scheduleRecords]);
    } catch (error) {
      console.error('Error fetching Spare Parts service history:', error);
      setSparePartsHistory([]);
    } finally {
      setSparePartsHistoryLoading(false);
    }
  };

  const combinedServiceHistory = useMemo(
    () => [...serviceHistory, ...sparePartsHistory].sort(
      (a, b) => new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime()
    ),
    [serviceHistory, sparePartsHistory]
  );

  const fetchBatteries = async () => {
    try {
      setBatteryLoading(true);
      const response = await batteryService.getBatteriesByVehicle(id!);
      console.log('Batteries received:', response);
      setBatteries(response || []);
    } catch (error: any) {
      console.error('Error fetching batteries:', error);
      setBatteries([]);
    } finally {
      setBatteryLoading(false);
    }
  };

  const fetchCurrentRiderName = async (riderId: string) => {
    try {
      console.log('Fetching rider data for ID:', riderId);
      const response = await riderService.getRiderById(riderId);
      console.log('Rider data received:', response);

      if (response.success && response.data) {
        const riderName = response.data.name || `${response.data.phone}`;
        console.log('Setting rider name:', riderName);
        setCurrentRiderName(riderName);

        // Set public ID if available
        if (response.data.publicRiderId) {
          console.log('Setting rider public ID:', response.data.publicRiderId);
          setCurrentRiderPublicId(response.data.publicRiderId);
        } else {
          setCurrentRiderPublicId(null);
        }
      } else {
        console.log('Rider API call failed or no data, using fallback:', riderId);
        setCurrentRiderName(riderId);
        setCurrentRiderPublicId(null);
      }
    } catch (error: any) {
      console.error('Error fetching rider:', error);
      // Keep the riderId as fallback
      setCurrentRiderName(riderId);
      setCurrentRiderPublicId(null);
    }
  };

  const fetchMediaFiles = async () => {
    try {
      setMediaLoading(true);
      const response = await vehicleService.getMediaFiles(id!);
      setMediaFiles(response.data || []);
    } catch (error: any) {
      console.error('Error fetching media files:', error);
      setError('Failed to fetch media files');
    } finally {
      setMediaLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available': return 'success';
      case 'Assigned': return 'primary';
      case 'Under Maintenance': return 'warning';
      case 'Retired': return 'default';
      case 'Damaged': return 'error';
      default: return 'default';
    }
  };

  const getBatteryStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'IN_STORAGE': return 'default';
      case 'UNDER_REPAIR': return 'warning';
      case 'DAMAGED': return 'error';
      case 'RETIRED': return 'default';
      default: return 'default';
    }
  };

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDocumentType = (mediaType: string): string => {
    if (!mediaType) return 'Document';

    // Convert technical names to readable titles
    const typeMap: { [key: string]: string } = {
      'insurance_document': 'Insurance Policy',
      'rc_document': 'RC Book',
      'registration_document': 'Registration Certificate',
      'vehicle_photo': 'Vehicle Photo',
      'puc_document': 'PUC Certificate',
      'fitness_document': 'Fitness Certificate',
      'permit_document': 'Permit',
      'service_photo': 'Service Photo',
      'damage_photo': 'Damage Photo',
      'rider_upload': 'Rider Upload',
    };

    // Check exact match first
    const exactMatch = typeMap[mediaType.toLowerCase()];
    if (exactMatch) return exactMatch;

    // Convert snake_case or underscore-separated to Title Case
    return mediaType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const renderMediaGrid = (mediaList: MediaFile[], title: string) => {
    if (mediaList.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No {title.toLowerCase()} available
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {mediaList.map((media) => (
          <Grid item xs={12} sm={6} md={4} key={media.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {media.fileType.startsWith('image/') ? (
                    <PhotoIcon color="primary" />
                  ) : (
                    <FileIcon color="action" />
                  )}
                  <Typography variant="subtitle2" sx={{ ml: 1, flex: 1 }} noWrap>
                    {formatDocumentType(media.mediaType)}
                  </Typography>
                </Box>

                {media.fileType.startsWith('image/') && media.url && (
                  <Box
                    component="img"
                    src={media.url}
                    alt={media.fileName}
                    sx={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      mb: 2
                    }}
                    onClick={() => handleImageClick(media.url!)}
                  />
                )}

                <Typography variant="caption" color="text.secondary" display="block">
                  {media.fileName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Size: {formatFileSize(media.fileSize)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Uploaded: {formatDate(media.uploadDate)}
                </Typography>

                {media.description && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {media.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewDocument(media)}
                    variant="outlined"
                    fullWidth
                  >
                    View
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !vehicle) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || 'Vehicle not found'}
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/vehicles')}
          sx={{ mt: 2 }}
        >
          Back to Vehicles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/vehicles')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Vehicle Profile
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/vehicles/edit/${vehicle.id}`)}
        >
          Edit Vehicle
        </Button>
      </Box>

      {/* Main Content with Tabs */}
      <Card variant="outlined">
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="vehicle profile tabs">
            <Tab label="Basic Information" />
            <Tab label="Service History" />
            <Tab label="Batteries" />
            <Tab label="Rider History" />
            <Tab label={`All Documents (${freshMediaFiles.length})`} />
          </Tabs>
        </Box>

        {/* Basic Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <CardContent>
            {/* Vehicle Overview */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                        <VehicleIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" gutterBottom>
                          {vehicle.registrationNumber}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {vehicle.model?.oem?.name} {vehicle.model?.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                      <Chip
                        label={vehicle.operationalStatus}
                        color={getStatusColor(vehicle.operationalStatus) as any}
                        variant="filled"
                      />
                      <Chip
                        label={vehicle.serviceStatus}
                        color={vehicle.serviceStatus === 'Active' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Color</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.color}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Year</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.year || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Mileage</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.mileage} km</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Assigned Hub</Typography>
                        <Typography variant="body2" fontWeight="medium" color="primary.main">
                          {(vehicle.hub as any)?.hubName || vehicle.hub?.name || 'Not assigned'}
                        </Typography>
                        {vehicle.hub?.address && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            📍 {vehicle.hub.address}
                          </Typography>
                        )}
                        {vehicle.hub?.city && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            🏙️ {vehicle.hub.city.name || vehicle.hub.city.displayName}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Vehicle Specifications */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Vehicle Specifications</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Chassis Number</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.chassisNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Battery Number</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.batteryNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Variant</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.variant || 'N/A'}</Typography>
                  </Grid>
                  {vehicle.batteryCapacity && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Battery Capacity</Typography>
                      <Typography variant="body2" fontWeight="medium">{vehicle.batteryCapacity} kWh</Typography>
                    </Grid>
                  )}
                  {vehicle.maxRange && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Max Range</Typography>
                      <Typography variant="body2" fontWeight="medium">{vehicle.maxRange} km</Typography>
                    </Grid>
                  )}
                  {vehicle.maxSpeed && (
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary">Max Speed</Typography>
                      <Typography variant="body2" fontWeight="medium">{vehicle.maxSpeed} km/h</Typography>
                    </Grid>
                  )}
                  {(vehicle as any).chargerNumber && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Charger Number</Typography>
                      <Typography variant="body2" fontWeight="medium">{(vehicle as any).chargerNumber}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Hub Assignment Details */}
            {vehicle.hub && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Assigned Hub Details</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Hub Name</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {(vehicle.hub as any)?.hubName || vehicle.hub?.name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Hub Code</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {(vehicle.hub as any)?.hubCode || vehicle.hub?.code || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">City</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {vehicle.hub.city?.name || vehicle.hub.city?.displayName || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="caption" color="text.secondary">Address</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {vehicle.hub.address || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Hub Type</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {vehicle.hub.hubType || 'N/A'}
                      </Typography>
                    </Grid>
                    {vehicle.hub.contactNumber && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Contact Number</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.hub.contactNumber}</Typography>
                      </Grid>
                    )}
                    {vehicle.hub.managerName && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Manager</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.hub.managerName}</Typography>
                      </Grid>
                    )}
                    {vehicle.hub.vehicleCapacity && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Vehicle Capacity</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.hub.vehicleCapacity} vehicles</Typography>
                      </Grid>
                    )}
                    {vehicle.hub.chargingPoints && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Charging Points</Typography>
                        <Typography variant="body2" fontWeight="medium">{vehicle.hub.chargingPoints}</Typography>
                      </Grid>
                    )}
                    {vehicle.hub.hasChargingStation !== undefined && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Charging Station</Typography>
                        <Chip
                          label={vehicle.hub.hasChargingStation ? 'Available' : 'Not Available'}
                          color={vehicle.hub.hasChargingStation ? 'success' : 'default'}
                          size="small"
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Show message if no hub assigned */}
            {!vehicle.hub && (
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'warning.light', borderColor: 'warning.main' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="warning.contrastText">Hub Assignment</Typography>
                  <Typography color="warning.contrastText">
                    🚨 This vehicle is not assigned to any hub. Please assign it to a hub for proper operation.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Registration Details */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Registration Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">RC Number</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.rcDetails?.rcNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">Registration Date</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.registrationDate ? formatDate(vehicle.registrationDate) : 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">RC Valid Upto</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.rcDetails?.validUpto ? formatDate(vehicle.rcDetails.validUpto) : 'N/A'}</Typography>
                  </Grid>
                  {vehicle.purchaseDate && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Purchase Date</Typography>
                      <Typography variant="body2" fontWeight="medium">{formatDate(vehicle.purchaseDate)}</Typography>
                    </Grid>
                  )}
                  {vehicle.purchasePrice && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Purchase Price</Typography>
                      <Typography variant="body2" fontWeight="medium">₹{vehicle.purchasePrice.toLocaleString()}</Typography>
                    </Grid>
                  )}
                  {vehicle.currentValue && (
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" color="text.secondary">Current Value</Typography>
                      <Typography variant="body2" fontWeight="medium">₹{vehicle.currentValue.toLocaleString()}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Insurance Details */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Insurance Details</Typography>
                {vehicle.insuranceDetails && vehicle.insuranceDetails.length > 0 ? (
                  vehicle.insuranceDetails.map((insurance, index) => (
                    <Card variant="outlined" key={index} sx={{ mb: index < vehicle.insuranceDetails!.length - 1 ? 2 : 0, bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom color="primary.main">
                          Policy #{index + 1} • {insurance.insuranceType || 'Comprehensive'}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Provider</Typography>
                            <Typography variant="body2" fontWeight="medium">{insurance.providerName}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Policy Number</Typography>
                            <Typography variant="body2" fontWeight="medium">{insurance.policyNumber}</Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Start Date</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(insurance.policyStartDate)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Expiry Date</Typography>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={new Date(insurance.policyEndDate) < new Date() ? 'error.main' : 'text.primary'}
                            >
                              {formatDate(insurance.policyEndDate)}
                              {new Date(insurance.policyEndDate) < new Date() && (
                                <Chip label="Expired" color="error" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Premium Amount</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              ₹{insurance.premiumAmount?.toLocaleString() || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Coverage Amount</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              ₹{insurance.coverageAmount?.toLocaleString() || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Chip
                              label={insurance.isActive ? 'Active' : 'Inactive'}
                              color={insurance.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6} sm={4}>
                            <Typography variant="caption" color="text.secondary">Verification</Typography>
                            <Chip
                              label={insurance.verificationStatus}
                              color={insurance.verificationStatus === 'Verified' ? 'success' :
                                     insurance.verificationStatus === 'Rejected' ? 'error' : 'warning'}
                              size="small"
                            />
                          </Grid>
                          {insurance.renewalReminder && (
                            <Grid item xs={6} sm={4}>
                              <Typography variant="caption" color="text.secondary">Renewal Reminder</Typography>
                              <Chip label="Enabled" color="info" size="small" />
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card variant="outlined" sx={{ bgcolor: 'error.light', borderColor: 'error.main' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="error.contrastText">⚠️ No Insurance Coverage</Typography>
                      <Typography color="error.contrastText">
                        This vehicle does not have any insurance details on record. This is a compliance risk and should be addressed immediately.
                      </Typography>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="error.contrastText">
                          Required actions:
                        </Typography>
                        <Typography variant="body2" color="error.contrastText">
                          • Add valid insurance policy details
                        </Typography>
                        <Typography variant="body2" color="error.contrastText">
                          • Upload insurance certificate documents
                        </Typography>
                        <Typography variant="body2" color="error.contrastText">
                          • Verify coverage amounts and validity
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>

            {/* Service Information Summary */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>Service Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">Last Service Date</Typography>
                    <Typography variant="body2" fontWeight="medium">{vehicle.lastServiceDate ? formatDate(vehicle.lastServiceDate) : 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">Next Service Due</Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={vehicle.nextServiceDue && new Date(vehicle.nextServiceDue) < new Date() ? 'error.main' : 'text.primary'}
                    >
                      {vehicle.nextServiceDue ? formatDate(vehicle.nextServiceDue) : 'N/A'}
                      {vehicle.nextServiceDue && new Date(vehicle.nextServiceDue) < new Date() && (
                        <Chip label="Overdue" color="error" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="text.secondary">Current Rider</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {vehicle.currentRiderId ? (
                        <Box>
                          <Link
                            component="button"
                            variant="body2"
                            fontWeight="medium"
                            onClick={() => navigate(`/rider-management/${vehicle.currentRiderId}`)}
                            sx={{
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                              cursor: 'pointer',
                              textAlign: 'left',
                              p: 0,
                              border: 'none',
                              background: 'none'
                            }}
                          >
                            {currentRiderPublicId || currentRiderName || vehicle.currentRiderId}
                          </Link>
                          {currentRiderName && currentRiderPublicId && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {currentRiderName}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" fontWeight="medium">
                          Not assigned
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </CardContent>
        </TabPanel>

        {/* Service History Tab */}
        <TabPanel value={tabValue} index={1}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Service History</Typography>
            {serviceLoading || sparePartsHistoryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : combinedServiceHistory.length > 0 ? (
              <Box>
                {combinedServiceHistory.map((service, index) => (
                  <Card key={service.id || index} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" color="primary" gutterBottom sx={{ textTransform: service.source === 'SPARE_PARTS' ? 'capitalize' : 'none' }}>
                            {service.serviceType}
                          </Typography>
                          {service.referenceCode && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Spare Parts reference: {service.referenceCode}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {service.description}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          {service.source === 'SPARE_PARTS' && <Chip label="Spare Parts" color="primary" variant="outlined" size="small" />}
                          <Chip
                            label={service.serviceStatus.replace(/_/g, ' ')}
                            color={
                              ['COMPLETED', 'CLOSED', 'RETURN_VERIFIED', 'VERIFIED', 'ACTIVE'].includes(service.serviceStatus.toUpperCase()) ? 'success' :
                              ['IN_PROGRESS', 'ASSIGNED', 'RAISED', 'DUE', 'IN_HOUSE_CREATED', 'SCHEDULED'].includes(service.serviceStatus.toUpperCase()) ? 'warning' :
                              service.serviceStatus.toUpperCase() === 'CANCELLED' ? 'error' : 'default'
                            }
                            size="small"
                          />
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Service Date</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {service.serviceDate ? formatDate(service.serviceDate) : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            {service.source === 'SPARE_PARTS' ? 'Total Maintenance Cost' : 'Cost'}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {service.cost != null ? `₹${service.cost.toLocaleString()}` : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Service Center</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {service.serviceCenter || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <Typography variant="caption" color="text.secondary">Technician</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {service.technicianName || 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>

                      {service.workPerformed && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">Work Performed</Typography>
                          <Typography variant="body2">
                            {service.workPerformed}
                          </Typography>
                        </Box>
                      )}

                      {service.nextServiceDate && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">Next Service Due</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(service.nextServiceDate)}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No service history available for this vehicle
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Service records will appear here once maintenance activities are logged
                </Typography>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* Batteries Tab */}
        <TabPanel value={tabValue} index={2}>
          <CardContent>
            {batteryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : batteries.length > 0 ? (
              <Grid container spacing={3}>
                {batteries.map((battery) => (
                  <Grid item xs={12} md={6} key={battery.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              {battery.slotPosition ? `Battery ${battery.slotPosition}` : 'Battery'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {battery.batteryNumber}
                            </Typography>
                          </Box>
                          <Chip
                            label={battery.status}
                            color={getBatteryStatusColor(battery.status)}
                            size="small"
                          />
                        </Box>

                        {/* Health Indicator */}
                        {battery.healthPercentage !== undefined && (
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">Battery Health</Typography>
                              <Typography variant="body2" fontWeight="medium" color={
                                battery.healthPercentage >= 90 ? 'success.main' :
                                battery.healthPercentage >= 70 ? 'warning.main' : 'error.main'
                              }>
                                {battery.healthPercentage.toFixed(1)}%
                              </Typography>
                            </Box>
                            <Box sx={{
                              width: '100%',
                              height: 8,
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                              overflow: 'hidden'
                            }}>
                              <Box sx={{
                                width: `${battery.healthPercentage}%`,
                                height: '100%',
                                bgcolor: battery.healthPercentage >= 90 ? 'success.main' :
                                         battery.healthPercentage >= 70 ? 'warning.main' : 'error.main',
                                transition: 'width 0.3s ease'
                              }} />
                            </Box>
                          </Box>
                        )}

                        <Grid container spacing={2}>
                          {battery.manufacturer && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Manufacturer</Typography>
                              <Typography variant="body2" fontWeight="medium">{battery.manufacturer}</Typography>
                            </Grid>
                          )}
                          {battery.batteryType && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Type</Typography>
                              <Typography variant="body2" fontWeight="medium">{battery.batteryType}</Typography>
                            </Grid>
                          )}
                          {battery.capacity && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Capacity</Typography>
                              <Typography variant="body2" fontWeight="medium">{battery.capacity} kWh</Typography>
                            </Grid>
                          )}
                          {battery.voltage && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Voltage</Typography>
                              <Typography variant="body2" fontWeight="medium">{battery.voltage} V</Typography>
                            </Grid>
                          )}
                          {battery.cycleCount !== undefined && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Cycle Count</Typography>
                              <Typography variant="body2" fontWeight="medium">{battery.cycleCount.toLocaleString()}</Typography>
                            </Grid>
                          )}
                          {battery.lastChargeDate && (
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Last Charged</Typography>
                              <Typography variant="body2" fontWeight="medium">{formatDate(battery.lastChargeDate)}</Typography>
                            </Grid>
                          )}
                          {battery.warrantyEndDate && (
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Warranty Valid Until</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {formatDate(battery.warrantyEndDate)}
                                {new Date(battery.warrantyEndDate) < new Date() && (
                                  <Chip label="Expired" color="error" size="small" sx={{ ml: 1 }} />
                                )}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        {/* Parent Vehicle Info (if different from current) */}
                        {battery.parentVehicleId !== battery.currentVehicleId && (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <Typography variant="caption" color="info.dark" display="block">
                              Originally from vehicle: {battery.parentVehicleId}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No batteries registered for this vehicle
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Battery information will appear here once batteries are registered
                </Typography>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* Rider History Tab */}
        <TabPanel value={tabValue} index={3}>
          <RiderHistoryTimeline vehicleId={vehicle.id} />
        </TabPanel>

        {mediaLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* All Documents Tab */}
            <TabPanel value={tabValue} index={4}>
              {renderMediaGrid(freshMediaFiles, 'All Documents')}
            </TabPanel>
          </>
        )}
      </Card>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Image Preview</Typography>
            <IconButton onClick={() => setSelectedImage(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Preview"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VehicleProfile;
